// The list sections of the Relationship drawer, in mock order: Cited
// Documents (expandable), Across-Document Citations, Within-Document
// References and Semantic Relationships. Each owns only its filter state; the
// drawer remounts them per document, so filters never leak between documents.
import { useMemo, useState } from "react";
import {
  Building2,
  ChevronDown,
  FileQuestion,
  FileText,
  Landmark,
  Network,
  Scale,
  Target,
} from "lucide-react";
import { DataTable } from "../../../components/DataTable";
import { toTitleCaseFromSnake } from "../utils";
import type { CitationRow, CitedDoc, InternalRefRow, SemanticRow } from "./utilities/types";
import {
  CITED_DOC_ICON_KEY,
  CITED_STATUS_LABEL,
  FILTER_ALL,
  REL_COLOR,
  REL_FOCUS_RING_CLS,
  RELATIONSHIP_COPY,
  RELATIONSHIP_SECTIONS,
  RELATIONSHIP_TABLE_PAGE_SIZE,
  TABLE_ITEM_LABELS,
  UNNAMED_TARGET,
  type CitedDocIconKey,
} from "./utilities/constants";
import { citationColumns, internalRefColumns, semanticColumns } from "./utilities/columns";
import {
  countBy,
  matchesQuery,
  pct,
  pluralSuffix,
  typeHex,
  typeLabel,
} from "./utilities/helpers";
import { FilterPill, RelCard, RelEmpty, SearchBox } from "./parts";
import type { LucideIcon } from "lucide-react";

const TABLE_PROPS = {
  compact: true,
  pageSize: RELATIONSHIP_TABLE_PAGE_SIZE,
  pagerStyle: "simple" as const,
};

const statusLabel = (status: string) => toTitleCaseFromSnake(status);

/* ── Cited documents ─────────────────────────────────────────────── */

const CITED_DOC_ICON_MAP: Record<CitedDocIconKey, LucideIcon> = {
  landmark: Landmark,
  network: Network,
  scale: Scale,
  building: Building2,
  fallback: FileQuestion,
};

function citedDocIcon(type: string): LucideIcon {
  const key = CITED_DOC_ICON_KEY[type] ?? "fallback";
  return CITED_DOC_ICON_MAP[key];
}

export function CitedDocumentsSection({ docs, kind }: { docs: CitedDoc[]; kind: string }) {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <RelCard
      title={RELATIONSHIP_SECTIONS.cited.title}
      dot={RELATIONSHIP_SECTIONS.cited.dot}
      right={`Documents this ${kind} references`}
      flush
    >
      {docs.length === 0 ? (
        <RelEmpty label={RELATIONSHIP_COPY.noCitedDocs} />
      ) : (
        docs.map((doc) => {
          const expanded = open.has(doc.key);
          const statusColor = doc.resolved ? REL_COLOR.green : REL_COLOR.red;
          const Icon = citedDocIcon(doc.type);
          return (
            <div
              key={doc.key}
              className="border-b border-l-[3px] border-b-border last:border-b-0"
              style={{ borderLeftColor: statusColor }}
            >
              <button
                type="button"
                onClick={() => toggle(doc.key)}
                aria-expanded={expanded}
                className={`flex w-full items-center gap-3 px-[18px] py-3.5 text-left transition-colors hover:bg-muted/30 ${REL_FOCUS_RING_CLS}`}
              >
                <span
                  className="grid size-8 flex-none place-items-center rounded-lg"
                  style={{ background: `${typeHex(doc.type)}1f`, color: typeHex(doc.type) }}
                >
                  <Icon className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-foreground break-words">
                    {doc.name === UNNAMED_TARGET ? UNNAMED_TARGET : doc.name}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {doc.evidence.length} citation{pluralSuffix(doc.evidence.length)}
                    {doc.code ? ` · ${doc.code}` : ""}
                    {` · ${typeLabel(doc.type)}`}
                  </div>
                </div>
                <span
                  className="inline-flex flex-none items-center rounded border px-2 py-0.5 text-[10px] font-bold"
                  style={{ background: `${statusColor}18`, color: statusColor, borderColor: `${statusColor}33` }}
                >
                  {doc.resolved ? CITED_STATUS_LABEL.resolved : CITED_STATUS_LABEL.unresolved}
                </span>
                <ChevronDown
                  className={`size-4 flex-none text-muted-foreground transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className="grid transition-[grid-template-rows] duration-300 ease-out"
                style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
              >
                <div className="overflow-hidden">
                  <div className="flex flex-col gap-1.5 px-[18px] pb-3.5">
                    {doc.evidence.map((ev, i) => (
                      <div
                        key={`${doc.key}-${i}`}
                        className="rounded-md border-l-[3px] border-border bg-muted/40 px-3 py-2.5 text-xs leading-normal text-muted-foreground"
                      >
                        <span className="font-medium text-foreground">"{ev.text || "-"}"</span>
                        <div className="mt-1 flex flex-wrap gap-3 text-[10px] text-muted-foreground">
                          {ev.page !== null && (
                            <span className="inline-flex items-center gap-1">
                              <FileText className="size-3" /> p.{ev.page}
                            </span>
                          )}
                          {ev.section && <span>§{ev.section}</span>}
                          {ev.confidence !== null && (
                            <span className="inline-flex items-center gap-1">
                              <Target className="size-3" /> {pct(ev.confidence)}%
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </RelCard>
  );
}

/* ── Across-document citations ───────────────────────────────────── */

export function CrossDocumentSection({
  rows,
  resolution,
}: {
  rows: CitationRow[];
  resolution: string;
}) {
  const [status, setStatus] = useState<string>(FILTER_ALL);
  const [types, setTypes] = useState<Set<string>>(() => new Set());
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);

  const statusCounts = useMemo(() => countBy(rows, (r) => r.status), [rows]);
  const typeCounts = useMemo(
    () => countBy(rows, (r) => r.type).sort((a, b) => b[1] - a[1]),
    [rows],
  );
  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (status === FILTER_ALL || r.status === status) &&
          (types.size === 0 || types.has(r.type)) &&
          matchesQuery(query, r.evidence, r.target),
      ),
    [rows, status, types, query],
  );

  const reset = () => setPage(0);
  const toggleType = (type: string) => {
    setTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
    reset();
  };

  return (
    <RelCard
      title={RELATIONSHIP_SECTIONS.cross.title}
      dot={RELATIONSHIP_SECTIONS.cross.dot}
      right={
        <>
          {rows.length} total · resolution <b className="font-semibold text-foreground">{resolution}</b>
        </>
      }
    >
      <div className="mb-3.5 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex flex-wrap items-center gap-1.5">
          <FilterPill active={status === FILTER_ALL} label="All" count={rows.length} onClick={() => { setStatus(FILTER_ALL); reset(); }} />
          {statusCounts.map(([key, n]) => (
            <FilterPill key={key} active={status === key} label={statusLabel(key)} count={n} onClick={() => { setStatus(key); reset(); }} />
          ))}
        </div>
        <SearchBox value={query} onChange={(v) => { setQuery(v); reset(); }} placeholder={RELATIONSHIP_COPY.searchEvidence} />
      </div>
      {typeCounts.length > 0 && (
        <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
          <FilterPill active={types.size === 0} label="All" onClick={() => { setTypes(new Set()); reset(); }} />
          {typeCounts.map(([type, n]) => (
            <FilterPill
              key={type}
              active={types.has(type)}
              label={typeLabel(type)}
              count={n}
              dot={typeHex(type)}
              onClick={() => toggleType(type)}
            />
          ))}
        </div>
      )}
      {rows.length === 0 ? (
        <RelEmpty label={RELATIONSHIP_COPY.noCitations} />
      ) : (
        <DataTable
          {...TABLE_PROPS}
          columns={citationColumns}
          data={filtered}
          page={page}
          totalItems={filtered.length}
          onPageChange={setPage}
          itemLabel={TABLE_ITEM_LABELS.citations}
          emptyMessage={RELATIONSHIP_COPY.noRows}
          rowKey={(r, i) => `${r.target}-${r.evidence}-${i}`}
        />
      )}
    </RelCard>
  );
}

/* ── Within-document references ──────────────────────────────────── */

export function WithinDocumentSection({
  rows,
  methods,
}: {
  rows: InternalRefRow[];
  methods: string[];
}) {
  const [status, setStatus] = useState<string>(FILTER_ALL);
  const [page, setPage] = useState(0);
  const statusCounts = useMemo(() => countBy(rows, (r) => r.status), [rows]);
  const filtered = useMemo(
    () => (status === FILTER_ALL ? rows : rows.filter((r) => r.status === status)),
    [rows, status],
  );

  return (
    <RelCard
      title={RELATIONSHIP_SECTIONS.internal.title}
      dot={RELATIONSHIP_SECTIONS.internal.dot}
      right={`${rows.length} total${methods.length ? ` · detected by ${methods.join("/")}` : ""}`}
    >
      <div className="mb-3.5 flex flex-wrap items-center gap-1.5">
        <FilterPill active={status === FILTER_ALL} label="All" count={rows.length} onClick={() => { setStatus(FILTER_ALL); setPage(0); }} />
        {statusCounts.map(([key, n]) => (
          <FilterPill key={key} active={status === key} label={statusLabel(key)} count={n} onClick={() => { setStatus(key); setPage(0); }} />
        ))}
      </div>
      {rows.length === 0 ? (
        <RelEmpty label={RELATIONSHIP_COPY.noInternalRefs} />
      ) : (
        <DataTable
          {...TABLE_PROPS}
          columns={internalRefColumns}
          data={filtered}
          page={page}
          totalItems={filtered.length}
          onPageChange={setPage}
          itemLabel={TABLE_ITEM_LABELS.references}
          emptyMessage={RELATIONSHIP_COPY.noRows}
          rowKey={(r, i) => `${r.target}-${r.section}-${i}`}
        />
      )}
    </RelCard>
  );
}

/* ── Semantic relationships ──────────────────────────────────────── */

export function SemanticSection({
  rows,
  detectionLabel,
}: {
  rows: SemanticRow[];
  detectionLabel: string;
}) {
  const [type, setType] = useState<string>(FILTER_ALL);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);

  // Contradicts first, then by count (mock ordering).
  const typeCounts = useMemo(
    () =>
      countBy(rows, (r) => r.rel).sort(
        (a, b) =>
          Number(a[0] !== "CONTRADICTS") - Number(b[0] !== "CONTRADICTS") || b[1] - a[1],
      ),
    [rows],
  );
  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (type === FILTER_ALL || r.rel === type) &&
          matchesQuery(query, r.src, r.tgt, r.srcEvidence, r.tgtEvidence, r.why),
      ),
    [rows, type, query],
  );

  return (
    <RelCard
      title={RELATIONSHIP_SECTIONS.semantic.title}
      dot={RELATIONSHIP_SECTIONS.semantic.dot}
      right={`${rows.length}${detectionLabel ? ` · ${detectionLabel}` : ""}`}
    >
      <div className="mb-3.5 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex flex-wrap items-center gap-1.5">
          <FilterPill active={type === FILTER_ALL} label="All" count={rows.length} onClick={() => { setType(FILTER_ALL); setPage(0); }} />
          {typeCounts.map(([key, n]) => (
            <FilterPill key={key} active={type === key} label={typeLabel(key)} count={n} onClick={() => { setType(key); setPage(0); }} />
          ))}
        </div>
        <SearchBox value={query} onChange={(v) => { setQuery(v); setPage(0); }} placeholder={RELATIONSHIP_COPY.searchRelationships} />
      </div>
      {rows.length === 0 ? (
        <RelEmpty label={RELATIONSHIP_COPY.noSemantic} />
      ) : (
        <DataTable
          {...TABLE_PROPS}
          columns={semanticColumns}
          data={filtered}
          page={page}
          totalItems={filtered.length}
          onPageChange={setPage}
          itemLabel={TABLE_ITEM_LABELS.relationships}
          emptyMessage={RELATIONSHIP_COPY.noRows}
          rowClassName={(r) => (r.rel === "CONTRADICTS" ? "bg-red-500/[0.05]" : "")}
          rowKey={(r, i) => `${r.src}-${r.tgt}-${r.rel}-${i}`}
        />
      )}
    </RelCard>
  );
}
